// Where a decision's measurement really stands -- read ONLY from the backend's
// own measurement contract (decisions.py `_measurement`):
//   intervention          set when the prepared work was approved
//   next_check_blocked_by "watchlist_not_approved" while weekly checks are off
//   next_check            the next scheduled check (only while checks are on)
//   latest                the first scored check AFTER approval, if any
//   result                not_started | too_early | improving | no_change |
//                         worse | not_comparable
// Measuring needs two separate approvals: the work, and the questions Martian
// watches (which is what switches weekly checks on). Nothing here claims a
// state the backend has not reported.
import { formatDate } from './ui';
import { hasValidBaseline } from './resultsCopy';

const RESULT_KEYS = ['improving', 'no_change', 'worse', 'not_comparable'];

export const MEASUREMENT_STAGES = Object.freeze([
  'work_not_approved', 'awaiting_questions', 'eligible', 'measurement_started', 'results_available',
]);

export function measurementStage(m) {
  if (!m || !m.supported) {
    return { key: 'unsupported', label: 'Not measured', detail: "Martian can't measure this kind of decision yet." };
  }
  if (!m.intervention) {
    if (hasValidBaseline(m)) {
      return {
        key: 'work_not_approved', label: 'Work not approved yet',
        detail: 'A visibility baseline is recorded. No work has been approved yet, so this is not a business outcome.',
      };
    }
    return {
      key: 'work_not_approved', label: 'Work not approved yet',
      detail: 'Measuring needs two approvals: the work Martian prepared, and the questions it watches.',
    };
  }
  if (m.next_check_blocked_by === 'watchlist_not_approved') {
    return {
      key: 'awaiting_questions', label: 'Waiting for your questions',
      detail: 'The work is approved. Approve the questions Martian watches so weekly checks can measure the result.',
      action: { label: 'Approve your questions', to: '/business/watchlist' },
    };
  }
  if (RESULT_KEYS.includes(m.result)) {
    return { key: 'results_available', label: 'Result available', detail: 'Martian compared a check after your approval with the baseline before it.' };
  }
  if (m.latest) {
    return {
      key: 'measurement_started', label: 'Measuring',
      detail: 'A check has run since you approved the work. A result appears once it can be compared with the baseline.',
    };
  }
  return {
    key: 'eligible', label: 'Ready to measure',
    detail: m.next_check
      ? `Weekly checks are on. The first check since your approval is due ${formatDate(m.next_check)}.`
      : 'Weekly checks are on. The first check since your approval has not run yet.',
  };
}

// What to tell the founder right after approving work, from the monitoring
// state the backend already reported (decisions payload `monitoring`).
export function approvalMessage(monitoring) {
  if (monitoring?.recurring) {
    return { text: 'Approved. Weekly checks are on, so the next check will be compared with the baseline.', needsQuestions: false };
  }
  return { text: 'Approved. To measure the result, approve the questions Martian watches.', needsQuestions: true };
}
